import type { EnglishLevel } from '@/types/story.types';

/**
 * Preguntas de autoevaluación "can-do" (sembrado de nivel).
 *
 * Son las mismas 2 preguntas que el test de nivel muestra "antes de empezar"
 * (ver SEED en src/pages/student/LevelTestPage/levelTestBank.ts). Aquí se usan
 * en el onboarding para estimar y fijar el nivel de partida del alumno sin
 * necesidad de hacer el test completo.
 *
 * ⚠️ Los PESOS deben mantenerse sincronizados con el SEED del test: comparten la
 * misma escala θ (1–6) y la misma fórmula de estimación.
 */
export interface SeedOptionDef {
  /** Clave i18n de la etiqueta. */
  labelKey: string;
  /** Peso en la escala θ (1 = A1 … 6 = C2). */
  weight: number;
}

export interface SeedQuestionDef {
  /** Clave i18n del enunciado. */
  questionKey: string;
  options: SeedOptionDef[];
}

export const LEVEL_SEED_QUESTIONS: SeedQuestionDef[] = [
  {
    questionKey: 'levelSeed.q1.question',
    options: [
      { labelKey: 'levelSeed.q1.o1', weight: 1.5 },
      { labelKey: 'levelSeed.q1.o2', weight: 2.5 },
      { labelKey: 'levelSeed.q1.o3', weight: 3.5 },
      { labelKey: 'levelSeed.q1.o4', weight: 5.0 },
    ],
  },
  {
    questionKey: 'levelSeed.q2.question',
    options: [
      { labelKey: 'levelSeed.q2.o1', weight: 2.0 },
      { labelKey: 'levelSeed.q2.o2', weight: 3.5 },
      { labelKey: 'levelSeed.q2.o3', weight: 5.0 },
    ],
  },
];

const CEFR_LEVELS: EnglishLevel[] = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];
const clamp = (n: number): number => Math.max(1, Math.min(6, n));

/**
 * Clave en User.preferences donde guardamos el θ de sembrado, para que el test
 * de nivel reutilice el punto de partida del onboarding (o del último test) y no
 * vuelva a preguntar las 2 can-do.
 */
export const LEVEL_SEED_PREF_KEY = 'levelSeedTheta';

/** θ inicial = media de los pesos, acotada a [1, 6]. Misma fórmula que el test. */
export function computeSeedTheta(weights: number[]): number {
  return clamp(weights.reduce((s, w) => s + w, 0) / weights.length);
}

/** θ inicial → MCER redondeado. */
export function computeSeedLevel(weights: number[]): EnglishLevel {
  return CEFR_LEVELS[clamp(Math.round(computeSeedTheta(weights))) - 1]!;
}
